"use client";

import { RichText } from "@/composites/foundation";
import type { CatalogSpecification } from "@/domain/catalog";
import { Accordion, Button, Heading, Text } from "@pamperedchef/design-system";
import type { UseAndCareGuide } from "../types";
import * as styles from "./AboutTheProduct.module.scss";

/**
 * One Dimensions / Features / Care & Safety accordion body sourced from the
 * CMS `about_product` content block. `html` is a ready-to-render RTE HTML
 * string (`content-service` serializes the Contentstack JSON RTE upstream);
 * `defaultOpen` mirrors the author's per-accordion default-open flag.
 */
export interface AboutProductSection {
	html?: string;
	defaultOpen?: boolean;
}

/**
 * CMS-authored content for the accordions, normalized from the
 * `about_product` block by its block adapter. Dimensions and Features
 * render this HTML in place of the spec-derived bullets; Features renders
 * it below the spec-derived Materials.
 */
export interface AboutProductContent {
	dimensions?: AboutProductSection;
	features?: AboutProductSection;
	careSafety?: AboutProductSection;
	replacementParts?: AboutProductSection;
}

export type AboutProductSectionKey = keyof AboutProductContent;

const DEFAULT_SECTION_ORDER: readonly AboutProductSectionKey[] = [
	"dimensions",
	"features",
	"careSafety",
	"replacementParts"
];

export interface AboutTheProductProps {
	specification?: CatalogSpecification;
	/** CMS-authored accordion content from the `about_product` block. */
	aboutProduct?: AboutProductContent;
	sectionOrder?: readonly AboutProductSectionKey[];
	consultantSellingTips?: string;
	useAndCareGuide?: UseAndCareGuide;
	useAndCareHref?: string;
	/**
	 * Invoked synchronously when "View Use & Care Guide" is pressed, before
	 * the guide tab is opened — analytics must observe the click first.
	 */
	onUseAndCareClick?: () => void;
	className?: string;
}

/**
 * "About the Product" — the row of accordions below the PDP hero
 * (Dimensions, Features, Care & Safety, Replacement Parts, For Consultants)
 * plus the "View Use & Care Guide" affordance. Each accordion is omitted entirely when its
 * underlying source is empty; the whole composite collapses to `null` when
 * nothing at all is provided.
 *
 * Dimensions, Features and Care & Safety render the CMS `about_product` RTE
 * HTML (via the `RichText` composite). Features leads with the spec-derived
 * Materials bullet, then appends the authored features below it. The DS
 * `Accordion` owns the chevron + divider chrome; per-accordion default-open
 * state comes from the CMS `default_open` flags.
 */
export function AboutTheProduct({
	specification,
	aboutProduct,
	sectionOrder,
	consultantSellingTips,
	useAndCareGuide,
	useAndCareHref,
	onUseAndCareClick,
	className
}: AboutTheProductProps): JSX.Element | null {
	const built = buildSections(
		specification,
		aboutProduct,
		consultantSellingTips,
		sectionOrder
	);
	const sections = built.map(({ title, content }) => ({ title, content }));
	const initialOpen = built
		.map((section, index) => (section.defaultOpen ? index : -1))
		.filter((index) => index >= 0);
	const hasAnySection = sections.length > 0;
	const hasGuide = Boolean(useAndCareGuide?.title) && Boolean(useAndCareHref);

	if (!hasAnySection && !hasGuide) return null;

	return (
		<section
			className={[styles["about"], className ?? ""]
				.filter(Boolean)
				.join(" ")}
			data-component="AboutTheProduct"
			aria-labelledby="about-the-product-heading"
		>
			<Heading
				tag="h2"
				id="about-the-product-heading"
				className={styles["about__heading"]}
			>
				About the Product
			</Heading>

			{hasAnySection ? (
				<Accordion
					sections={sections}
					initialOpen={initialOpen}
					inline
				/>
			) : null}

			{hasGuide && useAndCareHref ? (
				<Button
					kind="secondary"
					size="lg"
					leadingIcon={{ name: "book" }}
					onPress={() => {
						onUseAndCareClick?.();
						openInNewTab(useAndCareHref);
					}}
					className={styles["about__guide-trigger"]}
				>
					View Use &amp; Care Guide
				</Button>
			) : null}
		</section>
	);
}

// react-aria onPress can popup-block window.open; a synthetic anchor
// click opens the guide tab reliably.
function openInNewTab(href: string): void {
	const link = document.createElement("a");
	link.href = href;
	link.target = "_blank";
	link.rel = "noopener noreferrer";
	document.body.appendChild(link);
	link.click();
	link.remove();
}

interface BuiltSection {
	title: string;
	content: React.ReactNode;
	defaultOpen?: boolean;
}

function hasRenderableHtml(html: string | undefined): html is string {
	if (!html) return false;
	if (/<(img|iframe|video|embed)\b/i.test(html)) return true;
	return (
		html
			.replace(/<[^>]*>/g, "")
			.replace(/&nbsp;/gi, " ")
			.trim().length > 0
	);
}

/**
 * Builds the array passed to DS `Accordion`. Each section returns `null`
 * when its source content is empty — those entries are filtered out so DS
 * never renders an empty title.
 */
function buildSections(
	spec: CatalogSpecification | undefined,
	aboutProduct: AboutProductContent | undefined,
	consultantSellingTips: string | undefined,
	sectionOrder: readonly AboutProductSectionKey[] | undefined
): BuiltSection[] {
	const renderers: Record<AboutProductSectionKey, () => BuiltSection | null> =
		{
			dimensions: () => renderDimensions(aboutProduct),
			features: () => renderFeatures(spec, aboutProduct),
			careSafety: () => renderCareAndSafety(aboutProduct),
			replacementParts: () => renderReplacementParts(aboutProduct)
		};
	const authored =
		sectionOrder && sectionOrder.length > 0
			? sectionOrder
			: DEFAULT_SECTION_ORDER;
	const keys = [
		...new Set([
			...authored,
			...DEFAULT_SECTION_ORDER.filter((key) => !authored.includes(key))
		])
	];
	return [
		...keys.map((key) => renderers[key]()),
		renderForConsultants(consultantSellingTips)
	].filter((section): section is BuiltSection => section !== null);
}

/** Renders an array of strings as the DS-styled bullet list shown in Features. */
function bulletList(items: string[]): JSX.Element {
	return (
		<ul className={styles["about__bullets"]}>
			{items.map((item, i) => (
				<li key={`${item}-${i}`}>
					<Text tag="span" styledAs="mdr">
						{item}
					</Text>
				</li>
			))}
		</ul>
	);
}

function renderDimensions(
	aboutProduct: AboutProductContent | undefined
): BuiltSection | null {
	const section = aboutProduct?.dimensions;
	const html = section?.html;
	if (!hasRenderableHtml(html)) return null;
	return {
		title: "Dimensions",
		content: <RichText content={html} />,
		defaultOpen: section?.defaultOpen
	};
}

function renderFeatures(
	spec: CatalogSpecification | undefined,
	aboutProduct: AboutProductContent | undefined
): BuiltSection | null {
	const section = aboutProduct?.features;
	const html = hasRenderableHtml(section?.html) ? section?.html : undefined;
	const materials = spec?.materials ?? [];
	if (materials.length === 0) {
		if (!html) return null;
		return {
			title: "Features",
			content: <RichText content={html} />,
			defaultOpen: section?.defaultOpen
		};
	}

	return {
		title: "Features",
		content: (
			<div>
				{bulletList([`Materials: ${materials.join(", ")}`])}
				{html ? <RichText content={html} /> : null}
			</div>
		),
		defaultOpen: section?.defaultOpen
	};
}

function renderCareAndSafety(
	aboutProduct: AboutProductContent | undefined
): BuiltSection | null {
	const careNotes = aboutProduct?.careSafety;
	const careHtml = careNotes?.html;
	if (!hasRenderableHtml(careHtml)) return null;

	return {
		title: "Care & Safety",
		content: <RichText content={careHtml} />,
		defaultOpen: careNotes?.defaultOpen
	};
}

function renderReplacementParts(
	aboutProduct: AboutProductContent | undefined
): BuiltSection | null {
	const section = aboutProduct?.replacementParts;
	const html = section?.html;
	if (!hasRenderableHtml(html)) return null;
	return {
		title: "Replacement Parts",
		content: <RichText content={html} />,
		defaultOpen: section?.defaultOpen
	};
}

function renderForConsultants(
	sellingTipsHtml: string | undefined
): BuiltSection | null {
	if (!hasRenderableHtml(sellingTipsHtml)) return null;
	return {
		title: "For Consultants",
		content: <RichText content={sellingTipsHtml} />
	};
}
